import { Check, CreditCard, Loader2 } from "lucide-react";
import { fmtDate, fmtGNF } from "@/lib/format";

const STEPS = [
  { key: "Commande reçue", label: "Commande reçue", text: "Nous avons bien reçu votre commande." },
  { key: "Paiement confirmé", label: "Paiement confirmé", text: "Votre règlement a été vérifié par EMPIRE-SHOP." },
  { key: "En préparation", label: "En préparation", text: "Vos articles sont rassemblés à l'entrepôt EMPIRE-SHOP." },
  { key: "Prête pour livraison", label: "Colis prêt", text: "Votre colis est emballé et étiqueté." },
  { key: "En livraison", label: "En livraison", text: "Le livreur est en route vers vous." },
  { key: "Livrée", label: "Livrée", text: "Commande remise. Merci de votre confiance !" },
];

export const OrderTracker = ({ order }) => {
  const history = order.history || [];
  const reached = new Set(history.map((h) => h.status));
  if (order.status === "Paiement en attente") reached.add("Commande reçue");
  const steps = order.payment_method === "cash_on_delivery"
    ? STEPS.filter((s) => s.key !== "Paiement confirmé")
    : STEPS;
  const currentIndex = steps.findIndex((s) => s.key === order.status);
  const atAt = (key) => history.find((h) => h.status === key)?.at;

  if (["Annulée", "Retournée"].includes(order.status))
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="order-tracker-cancelled">
        Commande {order.status.toLowerCase()}
        {atAt(order.status) ? ` le ${fmtDate(atAt(order.status))}` : ""}.
        {history.find((h) => h.status === order.status)?.note
          ? ` Motif : ${history.find((h) => h.status === order.status).note}`
          : ""}
      </div>
    );

  return (
    <div data-testid="order-tracker">
      {order.payment_status !== "payé" && order.payment_method !== "cash_on_delivery" && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="order-payment-pending">
          <CreditCard className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Paiement en attente de vérification.</strong> Montant : {fmtGNF(order.total)} par{" "}
            {order.payment_method_label}
            {order.payment_reference ? ` (réf. ${order.payment_reference})` : " — référence non transmise"}.
            La préparation démarre dès la confirmation de l'encaissement.
          </span>
        </div>
      )}
      {order.payment_status === "payé" && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="order-payment-confirmed">
          <Check className="h-4 w-4 shrink-0" /> Paiement confirmé — {fmtGNF(order.total)} ({order.payment_method_label})
        </div>
      )}

      <ol className="space-y-0">
        {steps.map((s, i) => {
          const done = reached.has(s.key) || (currentIndex >= 0 && i < currentIndex);
          const current = s.key === order.status;
          return (
            <li key={s.key} className="flex gap-3" data-testid={`order-step-${s.key}`}>
              <div className="flex flex-col items-center">
                <span
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 ${
                    current
                      ? "border-brand bg-brand text-white"
                      : done
                      ? "border-brand bg-brand-light text-brand"
                      : "border-slate-200 bg-white text-slate-300"
                  }`}
                >
                  {current ? <Loader2 className="h-3 w-3 animate-spin" /> : done ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                </span>
                {i < steps.length - 1 && <span className={`h-7 w-0.5 ${done ? "bg-brand" : "bg-slate-200"}`} />}
              </div>
              <div className="pb-2">
                <p className={`text-sm font-semibold ${current ? "text-brand" : done ? "text-slate-900" : "text-slate-400"}`}>
                  {s.label}
                  {atAt(s.key) && <span className="ml-2 text-[11px] font-normal text-slate-500">{fmtDate(atAt(s.key))}</span>}
                </p>
                <p className={`text-xs ${current || done ? "text-slate-600" : "text-slate-400"}`}>{s.text}</p>
              </div>
            </li>
          );
        })}
      </ol>
      {order.courier_name && ["En livraison", "Prête pour livraison"].includes(order.status) && (
        <p className="mt-2 text-xs text-slate-600" data-testid="order-courier">
          Livreur assigné : <strong>{order.courier_name}</strong>
        </p>
      )}
    </div>
  );
};
